import { X, FilePdf } from "@phosphor-icons/react";

const Page = ({ children, n }) => (
  <div className="mx-auto w-full max-w-2xl rounded-lg bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.12)] sm:p-10" data-testid={`preview-page-${n}`}>
    <div className="mb-6 flex items-center justify-between border-b-4 border-pine pb-3">
      <span className="font-heading text-lg font-extrabold text-ink">Local<span className="text-pine">Lift</span></span>
      <span className="text-xs text-soot">Google Business Profile Audit</span>
    </div>
    {children}
    <p className="mt-8 border-t border-pine-soft pt-3 text-[10px] text-soot">
      Internal completeness assessment — not an official Google ranking score. · Page {n} of 3
    </p>
  </div>
);

const H2 = ({ children }) => (
  <h3 className="mt-5 mb-2 font-heading text-base font-extrabold text-ink">{children}</h3>
);

const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

export const ReportPreview = ({ lead, edit, settings, onClose }) => {
  const p = edit.place;
  const cats = (p.categories || "").split(",").map((s) => s.trim()).filter(Boolean);
  const comps = edit.competitors.filter((c) => (c.name || "").trim());
  const scored = edit.categories.filter((c) => c.status === "scored");
  const earned = scored.reduce((s, c) => s + (Number(c.points) || 0), 0);
  const possible = scored.reduce((s, c) => s + (Number(c.max) || 0), 0);
  const total = possible ? Math.round((100 * earned) / possible) : null;
  const price = settings?.price_makeover ?? 249;
  const website = settings?.website_url || "";

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-ink/70 p-4 backdrop-blur-sm sm:p-8" data-testid="report-preview">
      <div className="mx-auto mb-6 flex max-w-2xl items-center justify-between">
        <p className="font-heading text-lg font-bold text-cream">Report preview — reflects your current edits (save before approving)</p>
        <button data-testid="preview-close-button" onClick={onClose} className="flex items-center gap-1.5 rounded-full bg-cream px-4 py-2 text-sm font-bold text-ink transition-transform hover:scale-105">
          <X size={16} weight="bold" /> Close
        </button>
      </div>

      <div className="space-y-8 pb-16">
        <Page n={1}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">Google Business Profile Audit</p>
          <h2 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-ink">{p.name || lead.business_name}</h2>
          <p className="mt-1 text-xs text-soot">Prepared for {lead.contact_name || "—"}</p>
          <table className="mt-5 w-full text-xs">
            <tbody>
              {[
                ["Address", p.address || "Not found"],
                ["Phone", p.phone || "Missing"],
                ["Website", p.website || "Missing"],
                ["Categories", cats.join(", ") || "Not found"],
                ["Opening hours", p.hours.trim() ? "Published" : "Not published"],
                ["Rating", p.rating !== "" && p.rating !== null ? `${p.rating} / 5 from ${p.rating_count || 0} reviews` : "No reviews yet"],
                ["Photos", p.photos_count === "" || p.photos_count === null ? "—" : String(p.photos_count)],
              ].map(([k, v], i) => (
                <tr key={k} className={i % 2 ? "bg-white" : "bg-cream"}>
                  <td className="px-3 py-2 font-bold text-soot">{k}</td>
                  <td className="px-3 py-2 text-ink">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-5 flex items-center gap-5 rounded-xl bg-pine-light p-5">
            <span className="font-heading text-5xl font-extrabold text-pine">{total === null ? "—" : total}</span>
            <span className="text-xs text-soot">/ 100 — {total === null ? "not scored yet; unverified criteria are excluded, never penalized." : "internal completeness score, not a Google ranking score."}</span>
          </div>
          <H2>Score breakdown</H2>
          <ul className="space-y-1.5 text-xs">
            {edit.categories.map((c) => (
              <li key={c.key} className="text-soot">
                <span className="font-semibold text-ink">{c.label}:</span>{" "}
                {c.status === "scored" ? `${c.points}/${c.max}` : c.status === "needs_review" ? "Needs manual review" : "Not verified"}
                {c.note ? ` — ${c.note}` : ""}
              </li>
            ))}
          </ul>
          <H2>Key findings</H2>
          {edit.executive_summary && <p className="text-xs leading-relaxed text-soot">{edit.executive_summary}</p>}
          <ul className="mt-2 space-y-1 text-xs text-soot">
            {edit.opportunities.slice(0, 3).map((o, i) => <li key={i}>• <b className="text-ink">{o.title}</b> ({o.priority} priority)</li>)}
            {edit.manual_findings.map((f, i) => <li key={`f${i}`}>• <b className="text-ink">Manual finding:</b> {f}</li>)}
          </ul>
        </Page>

        <Page n={2}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">Competitor comparison</p>
          <h2 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-ink">How Your Listing Compares</h2>
          {comps.length ? (
            <table className="mt-5 w-full text-xs">
              <thead>
                <tr className="bg-pine text-cream">
                  <th className="px-3 py-2 text-left font-bold">Business</th>
                  <th className="px-3 py-2 text-left font-bold">Rating</th>
                  <th className="px-3 py-2 text-left font-bold">Reviews</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-pine-light">
                  <td className="px-3 py-2 font-semibold text-ink">{(p.name || "Your business") + " (you)"}</td>
                  <td className="px-3 py-2 text-ink">{p.rating === "" || p.rating === null ? "—" : p.rating}</td>
                  <td className="px-3 py-2 text-ink">{p.rating_count === "" || p.rating_count === null ? "—" : p.rating_count}</td>
                </tr>
                {comps.map((c, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-cream"}>
                    <td className="px-3 py-2 text-ink">{c.name}</td>
                    <td className="px-3 py-2 text-ink">{c.rating ?? "—"}</td>
                    <td className="px-3 py-2 text-ink">{c.rating_count ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="mt-4 text-xs text-soot">No competitor data — comparison needs manual research.</p>
          )}
          {edit.competitor_comparison && <p className="mt-4 text-xs leading-relaxed text-soot">{edit.competitor_comparison}</p>}
          <H2>Supporting evidence</H2>
          <ul className="space-y-1.5 text-xs text-soot">
            <li>• <b className="text-ink">Source of business data:</b> manual research collected and verified by the auditor.</li>
            <li>• <b className="text-ink">Recommendations:</b> drafted from the verified data and reviewed by a human before sending.</li>
            <li>• <b className="text-ink">Needs verification:</b> items marked "Needs manual review" or "Not verified" are excluded from the score.</li>
          </ul>
        </Page>

        <Page n={3}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">Priority recommendations</p>
          <h2 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-ink">What To Fix First</h2>
          <div className="mt-4 space-y-3">
            {edit.opportunities.slice(0, 4).map((o, i) => (
              <div key={i}>
                <p className="text-xs"><b className="text-pine">{i + 1}. {o.title}</b> <span className="text-[10px] text-soot">[{o.priority.toUpperCase()} PRIORITY]</span></p>
                <p className="text-xs leading-relaxed text-soot">{o.why}</p>
              </div>
            ))}
          </div>
          {lines(edit.next_steps).length > 0 && (
            <>
              <H2>Recommended next steps</H2>
              <ul className="space-y-1 text-xs text-soot">
                {lines(edit.next_steps).slice(0, 4).map((s, i) => <li key={i}>• {s}</li>)}
              </ul>
            </>
          )}
          {edit.before_after.some((b) => b.before || b.after) && (
            <>
              <H2>Proposed copywriting improvements</H2>
              <div className="space-y-3">
                {edit.before_after.filter((b) => b.before || b.after).slice(0, 3).map((b, i) => (
                  <div key={i} className="text-xs">
                    <p className="text-soot"><span className="text-[10px] font-bold uppercase">Before:</span> {b.before}</p>
                    <p className="font-medium text-ink"><span className="text-[10px] font-bold uppercase text-pine">After:</span> {b.after}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="mt-6 rounded-xl bg-ink p-5 text-cream">
            <p className="font-heading text-sm font-extrabold">The Google Maps Profile Makeover — ${price} one-time</p>
            <p className="mt-2 text-[11px] leading-relaxed text-pine-light">
              Full profile audit · business description rewrite · up to five optimized service descriptions · category
              recommendations · four profile posts · review response templates · implementation & quality check.
            </p>
            {website && <p className="mt-3 text-[11px] font-bold">Get started: {website}</p>}
          </div>
        </Page>
      </div>
    </div>
  );
};
